<template>
  <div class="ops-dashboard">
    <header class="ops-dashboard__page-head">
      <div class="ops-dashboard__page-head-text">
        <h1 class="ops-dashboard__page-title">대시보드</h1>
        <!-- <p class="ops-dashboard__page-sub">
          선택한 기간의 매출·주문·채널 지표를 요약합니다.
        </p> -->
      </div>
      <div class="ops-dashboard__page-head-actions">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          unlink-panels
          range-separator="~"
          start-placeholder="시작일"
          end-placeholder="종료일"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          size="small"
          class="ops-dashboard__date-picker"
          @change="onDateRangeChange"
        />
        <el-button
          type="default"
          size="small"
          icon="el-icon-download"
          class="ops-dashboard__head-btn"
          @click="onDemoExport"
        >
          내보내기
        </el-button>
        <el-button
          type="default"
          size="small"
          icon="el-icon-document"
          class="ops-dashboard__head-btn"
          @click="onDemoReport"
        >
          리포트
        </el-button>
        <el-tooltip content="데이터를 다시 불러옵니다" placement="bottom">
          <el-button
            type="default"
            size="small"
            icon="el-icon-refresh"
            circle
            class="ops-dashboard__refresh"
            @click="onRefresh"
          />
        </el-tooltip>
      </div>
    </header>

    <section class="ops-dashboard__kpis">
      <el-card
        v-for="item in kpis"
        :key="item.key"
        class="ops-dashboard__kpi-card"
        shadow="hover"
      >
        <p class="ops-dashboard__kpi-label">{{ item.label }}</p>
        <p class="ops-dashboard__kpi-value">{{ item.display }}</p>
        <p
          class="ops-dashboard__kpi-delta"
          :class="item.up ? 'is-up' : 'is-down'"
        >
          {{ item.delta }}
        </p>
      </el-card>
    </section>

    <el-row :gutter="20" class="ops-dashboard__row">
      <el-col :xs="24" :lg="16">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>{{ lineChartTitle }}</span>
          </div>
          <apexchart
            :key="'line-' + chartRevision"
            type="area"
            height="320"
            :options="lineChartOptions"
            :series="lineSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>채널별 매출 비중</span>
          </div>
          <apexchart
            :key="'donut-' + chartRevision"
            type="donut"
            height="320"
            :options="donutOptions"
            :series="donutSeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="ops-dashboard__row">
      <el-col :xs="24" :lg="14">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>카테고리별 주문 건수</span>
          </div>
          <apexchart
            :key="'bar-' + chartRevision"
            type="bar"
            height="300"
            :options="barOptions"
            :series="barSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card class="ops-dashboard__chart-card" shadow="never">
          <div slot="header" class="ops-dashboard__card-head">
            <span>최근 주문</span>
          </div>
          <el-table
            :data="recentOrders"
            stripe
            size="small"
            class="ops-dashboard__table"
            :show-header="true"
          >
            <el-table-column prop="id" label="주문번호" width="100" />
            <el-table-column prop="channel" label="채널" width="90" />
            <el-table-column prop="amount" label="금액" align="right" />
            <el-table-column label="상태" width="102" align="center">
              <template slot-scope="{ row }">
                <el-tag
                  :type="orderStatusTagType(row.status)"
                  size="mini"
                  effect="plain"
                  class="ops-dashboard__status-tag"
                >
                  {{ row.status }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <section class="ops-dashboard__section">
      <h2 class="ops-dashboard__section-title">상세 분석</h2>

      <el-row :gutter="20" class="ops-dashboard__row">
        <el-col :xs="24">
          <el-card class="ops-dashboard__chart-card" shadow="never">
            <div slot="header" class="ops-dashboard__card-head">
              <span>요일×시간 주문 밀도</span>
            </div>
            <apexchart
              :key="'heatmap-' + chartRevision"
              type="heatmap"
              height="380"
              :options="heatmapOptions"
              :series="heatmapSeries"
            />
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="ops-dashboard__row">
        <el-col :xs="24" :lg="12">
          <el-card class="ops-dashboard__chart-card" shadow="never">
            <div slot="header" class="ops-dashboard__card-head">
              <span>시간대별 주문 (0~23시)</span>
            </div>
            <apexchart
              :key="'hourly-' + chartRevision"
              type="bar"
              height="300"
              :options="hourlyChartOptions"
              :series="hourlySeries"
            />
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="12">
          <el-card class="ops-dashboard__chart-card" shadow="never">
            <div slot="header" class="ops-dashboard__card-head">
              <span>요일별 매출·주문</span>
            </div>
            <apexchart
              :key="'dow-' + chartRevision"
              type="bar"
              height="300"
              :options="dowChartOptions"
              :series="dowSeries"
            />
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="ops-dashboard__row">
        <el-col :xs="24" :lg="14">
          <el-card class="ops-dashboard__chart-card" shadow="never">
            <div slot="header" class="ops-dashboard__card-head">
              <span>인기 상품 TOP 5 (SKU)</span>
            </div>
            <el-table
              :data="topProducts"
              stripe
              size="small"
              class="ops-dashboard__table"
            >
              <el-table-column prop="sku" label="SKU" min-width="100" />
              <el-table-column prop="name" label="상품명" min-width="140" />
              <el-table-column
                prop="qty"
                label="판매수량"
                width="90"
                align="right"
              />
              <el-table-column
                prop="amount"
                label="매출"
                min-width="110"
                align="right"
              />
            </el-table>
          </el-card>
        </el-col>
        <el-col :xs="24" :lg="10">
          <el-card class="ops-dashboard__chart-card" shadow="never">
            <div slot="header" class="ops-dashboard__card-head">
              <span>전환 퍼널 (방문 → 장바구니 → 결제)</span>
            </div>
            <apexchart
              :key="'funnel-' + chartRevision"
              type="bar"
              height="320"
              :options="funnelChartOptions"
              :series="funnelSeries"
            />
          </el-card>
        </el-col>
      </el-row>
    </section>
  </div>
</template>

<script>
const PRIMARY = "#254aa5";
const PRIMARY_LIGHT = "#4a90e2";
const MUTED = "#8b95a5";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function defaultRange14d() {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 13);
  return [formatYmd(start), formatYmd(end)];
}

function buildCategories(start, end, n) {
  const startMs = start.getTime();
  const endMs = end.getTime();
  const out = [];
  const denom = Math.max(n - 1, 1);
  for (let i = 0; i < n; i++) {
    const t = new Date(startMs + ((endMs - startMs) * i) / denom);
    out.push(`${t.getMonth() + 1}/${t.getDate()}`);
  }
  return out;
}

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => String(i));
const DOW_LABELS = ["월", "화", "수", "목", "금", "토", "일"];
const DONUT_LABELS = ["자사몰", "오픈마켓", "SNS·라이브", "B2B"];

function buildDonutOptions(series) {
  const idSuffix = Array.isArray(series) ? series.join("-") : "";
  return {
    chart: {
      id: `ops-donut-${idSuffix}`,
      fontFamily: "Pretendard, sans-serif",
    },
    labels: [...DONUT_LABELS],
    colors: [PRIMARY, PRIMARY_LIGHT, "#67c23a", "#e6a23c"],
    legend: {
      position: "bottom",
      fontSize: "13px",
      labels: { colors: "#505258" },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            name: { show: true },
            value: {
              show: true,
              formatter: (val) => (val != null && val !== "" ? `${val}%` : ""),
            },
            total: {
              show: false,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
  };
}

/** 기간 길이에 따른 mock 묶음 (로컬 전용) */
const MOCK_PRESETS = {
  "7d": {
    kpis: [
      {
        key: "rev",
        label: "기간 매출(합)",
        display: "₩ 58,200,000",
        delta: "이전 7일 대비 +2.1%",
        up: true,
      },
      {
        key: "orders",
        label: "주문 건수",
        display: "412건",
        delta: "이전 7일 대비 +6건",
        up: true,
      },
      {
        key: "cvr",
        label: "전환율",
        display: "3.08%",
        delta: "이전 7일 대비 -0.05%p",
        up: false,
      },
      {
        key: "aov",
        label: "객단가",
        display: "₩ 31,200",
        delta: "이전 7일 대비 +1.2%",
        up: true,
      },
    ],
    revenue: [720, 810, 690, 880, 920, 850, 940],
    donut: [40, 30, 18, 12],
    bar: [360, 320, 250, 210, 160, 130],
    orders: [
      {
        id: "ORD-8102",
        channel: "자사몰",
        amount: "₩ 72,000",
        status: "결제완료",
      },
      {
        id: "ORD-8101",
        channel: "오픈마켓",
        amount: "₩ 198,000",
        status: "배송중",
      },
      {
        id: "ORD-8100",
        channel: "SNS",
        amount: "₩ 38,500",
        status: "결제완료",
      },
      {
        id: "ORD-8099",
        channel: "자사몰",
        amount: "₩ 289,000",
        status: "배송준비",
      },
      {
        id: "ORD-8098",
        channel: "B2B",
        amount: "₩ 980,000",
        status: "결제완료",
      },
    ],
    hourly: [
      8, 5, 4, 3, 4, 7, 12, 18, 24, 30, 36, 40, 42, 38, 32, 34, 36, 40, 44, 36,
      28, 20, 14, 10,
    ],
    dowSales: [58, 55, 62, 64, 68, 72, 54],
    dowOrders: [210, 198, 225, 235, 248, 262, 195],
    topProducts: [
      {
        sku: "SKU-7721",
        name: "라이트 윈드 점퍼",
        qty: 96,
        amount: "₩ 3,820,000",
      },
      {
        sku: "SKU-6612",
        name: "유기농 스낵 세트",
        qty: 88,
        amount: "₩ 1,980,000",
      },
      {
        sku: "SKU-9044",
        name: "실크 쿠션 커버",
        qty: 74,
        amount: "₩ 2,140,000",
      },
      {
        sku: "SKU-5510",
        name: "스테인리스 텀블러",
        qty: 62,
        amount: "₩ 1,240,000",
      },
      {
        sku: "SKU-3388",
        name: "미니 가습기",
        qty: 51,
        amount: "₩ 980,000",
      },
    ],
    funnel: [62000, 9800, 2150],
  },
  "14d": {
    kpis: [
      {
        key: "rev",
        label: "기간 매출(합)",
        display: "₩ 124,500,000",
        delta: "이전 14일 대비 +4.2%",
        up: true,
      },
      {
        key: "orders",
        label: "주문 건수",
        display: "384건",
        delta: "이전 14일 대비 +12건",
        up: true,
      },
      {
        key: "cvr",
        label: "전환율",
        display: "3.24%",
        delta: "이전 14일 대비 -0.1%p",
        up: false,
      },
      {
        key: "aov",
        label: "객단가",
        display: "₩ 32,400",
        delta: "이전 14일 대비 +2.1%",
        up: true,
      },
    ],
    revenue: [
      820, 910, 780, 960, 1020, 980, 1100, 1050, 1120, 1080, 1150, 1200, 1180,
      1240,
    ],
    donut: [42, 28, 18, 12],
    bar: [420, 380, 290, 240, 180, 150],
    orders: [
      {
        id: "ORD-9281",
        channel: "자사몰",
        amount: "₩ 89,000",
        status: "결제완료",
      },
      {
        id: "ORD-9280",
        channel: "오픈마켓",
        amount: "₩ 234,500",
        status: "배송중",
      },
      {
        id: "ORD-9279",
        channel: "SNS",
        amount: "₩ 45,200",
        status: "결제완료",
      },
      {
        id: "ORD-9278",
        channel: "자사몰",
        amount: "₩ 312,000",
        status: "배송준비",
      },
      {
        id: "ORD-9277",
        channel: "B2B",
        amount: "₩ 1,240,000",
        status: "결제완료",
      },
    ],
    hourly: [
      12, 8, 5, 4, 6, 9, 15, 22, 28, 35, 42, 48, 52, 45, 38, 40, 44, 50, 55, 48,
      38, 28, 18, 14,
    ],
    dowSales: [82, 78, 85, 88, 92, 95, 72],
    dowOrders: [420, 400, 430, 450, 480, 510, 380],
    topProducts: [
      {
        sku: "SKU-8821",
        name: "프리미엄 티셔츠",
        qty: 128,
        amount: "₩ 4,220,000",
      },
      {
        sku: "SKU-7730",
        name: "캔버스 스니커즈",
        qty: 96,
        amount: "₩ 6,720,000",
      },
      {
        sku: "SKU-6402",
        name: "허니 시리얼 3입",
        qty: 84,
        amount: "₩ 1,512,000",
      },
      {
        sku: "SKU-5218",
        name: "아로마 디퓨저",
        qty: 71,
        amount: "₩ 2,130,000",
      },
      {
        sku: "SKU-4091",
        name: "블루투스 이어폰",
        qty: 58,
        amount: "₩ 5,220,000",
      },
    ],
    funnel: [128000, 19200, 4380],
  },
  "30d": {
    kpis: [
      {
        key: "rev",
        label: "기간 매출(합)",
        display: "₩ 268,900,000",
        delta: "이전 30일 대비 +6.8%",
        up: true,
      },
      {
        key: "orders",
        label: "주문 건수",
        display: "842건",
        delta: "이전 30일 대비 +38건",
        up: true,
      },
      {
        key: "cvr",
        label: "전환율",
        display: "3.41%",
        delta: "이전 30일 대비 +0.12%p",
        up: true,
      },
      {
        key: "aov",
        label: "객단가",
        display: "₩ 33,800",
        delta: "이전 30일 대비 +0.9%",
        up: true,
      },
    ],
    revenue: [
      760, 800, 780, 820, 880, 900, 920, 890, 910, 950, 980, 1000, 1020, 1040,
    ],
    donut: [38, 26, 22, 14],
    bar: [480, 420, 340, 280, 210, 170],
    orders: [
      {
        id: "ORD-7011",
        channel: "오픈마켓",
        amount: "₩ 156,000",
        status: "배송중",
      },
      {
        id: "ORD-7010",
        channel: "자사몰",
        amount: "₩ 62,400",
        status: "결제완료",
      },
      {
        id: "ORD-7009",
        channel: "B2B",
        amount: "₩ 2,100,000",
        status: "결제완료",
      },
      {
        id: "ORD-7008",
        channel: "SNS",
        amount: "₩ 29,900",
        status: "취소요청",
      },
      {
        id: "ORD-7007",
        channel: "자사몰",
        amount: "₩ 445,000",
        status: "배송준비",
      },
    ],
    hourly: [
      18, 12, 8, 6, 8, 12, 20, 28, 36, 44, 52, 58, 62, 55, 48, 50, 54, 60, 66,
      58, 46, 34, 24, 20,
    ],
    dowSales: [92, 88, 94, 98, 102, 108, 85],
    dowOrders: [480, 460, 490, 510, 540, 580, 440],
    topProducts: [
      {
        sku: "SKU-A102",
        name: "프로 바운스 런닝화",
        qty: 214,
        amount: "₩ 14,980,000",
      },
      {
        sku: "SKU-B204",
        name: "리넨 셔츠 드레스",
        qty: 188,
        amount: "₩ 9,400,000",
      },
      {
        sku: "SKU-C330",
        name: "콜드브루 원두 500g",
        qty: 162,
        amount: "₩ 3,240,000",
      },
      {
        sku: "SKU-D412",
        name: "LED 스탠드",
        qty: 135,
        amount: "₩ 4,050,000",
      },
      {
        sku: "SKU-E501",
        name: "실크 수면안대",
        qty: 118,
        amount: "₩ 1,888,000",
      },
    ],
    funnel: [285000, 42800, 9820],
  },
};

function buildHourlyOptions() {
  return {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Pretendard, sans-serif",
    },
    plotOptions: {
      bar: {
        borderRadius: 3,
        columnWidth: "72%",
      },
    },
    colors: [PRIMARY_LIGHT],
    dataLabels: { enabled: false },
    xaxis: {
      categories: HOUR_LABELS,
      title: { text: "시간(시)", style: { color: MUTED, fontSize: "11px" } },
      labels: {
        style: { colors: MUTED, fontSize: "10px" },
        rotate: -45,
        rotateAlways: false,
      },
    },
    yaxis: {
      labels: {
        style: { colors: MUTED },
        formatter: (v) => (Number.isInteger(v) ? `${v}` : ""),
      },
      title: { text: "주문(건)", style: { color: MUTED, fontSize: "11px" } },
    },
    grid: { borderColor: "#eef0f3", strokeDashArray: 4 },
    tooltip: {
      y: { formatter: (v) => `${Math.round(v)}건` },
    },
  };
}

function buildDowOptions() {
  return {
    chart: {
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      fontFamily: "Pretendard, sans-serif",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "58%",
      },
    },
    colors: [PRIMARY, "#67c23a"],
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: DOW_LABELS,
      labels: { style: { colors: MUTED } },
    },
    yaxis: [
      {
        seriesName: "매출",
        title: {
          text: "매출(백만)",
          style: { color: MUTED, fontSize: "11px" },
        },
        labels: { style: { colors: MUTED } },
      },
      {
        seriesName: "주문",
        opposite: true,
        title: { text: "주문(건)", style: { color: MUTED, fontSize: "11px" } },
        labels: { style: { colors: MUTED } },
      },
    ],
    grid: { borderColor: "#eef0f3" },
    tooltip: {
      shared: true,
      intersect: false,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      labels: { colors: "#505258" },
    },
  };
}

/**
 * ApexCharts 퍼널: `type: 'bar'` + `plotOptions.bar.isFunnel`
 * 복구: 아래 `buildFunnelOptionsLegacy` 주석 블록을 함수 본문으로 옮기면 됩니다.
 */
// function buildFunnelOptions() {
//   return {
//     chart: {
//       type: "bar",
//       toolbar: { show: false },
//       fontFamily: "Pretendard, sans-serif",
//     },
//     plotOptions: {
//       bar: {
//         horizontal: true,
//         isFunnel: true,
//         isFunnel3d: true,
//         barHeight: "52%",
//         distributed: true,
//         borderRadius: 0,
//         dataLabels: { position: "center" },
//       },
//     },
//     colors: [PRIMARY, PRIMARY_LIGHT, "#67c23a"],
//     dataLabels: {
//       enabled: true,
//       formatter: (v) => formatFunnelVal(v),
//       style: { fontSize: "11px", colors: ["#fff"] },
//     },
//     xaxis: {
//       categories: ["방문(세션)", "장바구니 담기", "결제 완료"],
//       labels: {
//         style: { colors: MUTED },
//         formatter: (v) => formatFunnelAxis(v),
//       },
//     },
//     yaxis: {
//       labels: {
//         style: { colors: MUTED, fontSize: "12px", fontWeight: 500 },
//       },
//     },
//     grid: {
//       borderColor: "#eef0f3",
//       padding: { left: 4, right: 12 },
//     },
//     tooltip: {
//       theme: "light",
//       fillSeriesColor: false,
//       y: {
//         formatter: (v) => formatFunnelVal(v),
//       },
//     },
//     legend: { show: false },
//   };
// }

function buildFunnelOptionsLegacy() {
  return {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Pretendard, sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "52%",
        distributed: true,
        borderRadius: 6,
        dataLabels: { position: "center" },
      },
    },
    colors: [PRIMARY, PRIMARY_LIGHT, "#67c23a"],
    dataLabels: {
      enabled: true,
      formatter: (v) => formatFunnelVal(v),
      style: { fontSize: "11px", colors: ["#fff"] },
    },
    xaxis: {
      categories: ["방문(세션)", "장바구니 담기", "결제 완료"],
      labels: {
        style: { colors: MUTED },
        formatter: (v) => formatFunnelAxis(v),
      },
    },
    yaxis: {
      labels: {
        style: { colors: MUTED, fontSize: "12px", fontWeight: 500 },
      },
    },
    grid: { borderColor: "#eef0f3", xaxis: { lines: { show: true } } },
    tooltip: {
      y: {
        formatter: (v) => formatFunnelVal(v),
      },
    },
    legend: { show: false },
  };
}

function formatFunnelVal(v) {
  const n = Number(v);
  if (n >= 10000) {
    return `${(n / 10000).toFixed(1)}만`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(Math.round(n));
}

function formatFunnelAxis(v) {
  return formatFunnelVal(v);
}

const HEATMAP_DOW = ["월", "화", "수", "목", "금", "토", "일"];

/** 요일×시간(24) 격자 — 주중·점심·저녁 피크를 가정한 목업 밀도 */
function buildHeatmapSeries(seed) {
  return HEATMAP_DOW.map((name, d) => {
    const row = [];
    for (let h = 0; h < 24; h++) {
      const weekdayBoost = d >= 1 && d <= 5 ? 1.18 : 0.72;
      const lunch = h >= 11 && h <= 14 ? 1.22 : 1;
      const evening = h >= 19 && h <= 22 ? 1.18 : 1;
      const dawn = h >= 0 && h <= 6 ? 0.52 : 1;
      const rnd = (seed + d * 31 + h * 17) % 45;
      const v = Math.round(
        12 + weekdayBoost * lunch * evening * dawn * 38 + rnd * 0.9
      );
      row.push(Math.min(100, Math.max(3, v)));
    }
    return { name, data: row };
  });
}

function buildHeatmapOptions() {
  return {
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      fontFamily: "Pretendard, sans-serif",
    },
    dataLabels: { enabled: false },
    colors: [PRIMARY],
    xaxis: {
      type: "category",
      categories: Array.from({ length: 24 }, (_, i) => String(i)),
      labels: {
        rotate: -45,
        style: { colors: MUTED, fontSize: "10px" },
        maxHeight: 56,
        formatter: (val) => `${val}시`,
      },
    },
    yaxis: {
      labels: {
        style: { colors: MUTED, fontSize: "12px" },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.55,
        radius: 4,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: [
            { from: 0, to: 40, color: "#e8ecf4", name: "낮음" },
            { from: 41, to: 68, color: "#8fabdd", name: "중간" },
            { from: 69, to: 100, color: PRIMARY, name: "높음" },
          ],
        },
      },
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (v) => `${v} (상대 지수)`,
      },
    },
    legend: { show: false },
    grid: { padding: { right: 12 } },
  };
}

export default {
  name: "OperationsDashboard",
  data() {
    const baseLineOptions = {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "Pretendard, sans-serif",
      },
      colors: [PRIMARY],
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: [],
        labels: { style: { colors: MUTED } },
      },
      yaxis: {
        labels: {
          style: { colors: MUTED },
          formatter: (v) => `${v}`,
        },
      },
      grid: {
        borderColor: "#eef0f3",
        strokeDashArray: 4,
      },
      tooltip: {
        y: {
          formatter: (v) => `${v}백만`,
        },
      },
    };

    const p14 = MOCK_PRESETS["14d"];
    const heatSeed = 4283;

    return {
      dateRange: defaultRange14d(),
      chartRevision: 0,
      lineChartTitle: "매출 추이",
      kpis: p14.kpis.map((k) => ({ ...k })),
      lineSeries: [{ name: "매출(백만원)", data: [...p14.revenue] }],
      lineChartOptions: {
        ...baseLineOptions,
        xaxis: { ...baseLineOptions.xaxis },
      },
      donutSeries: [...p14.donut],
      barSeries: [{ name: "주문", data: [...p14.bar] }],
      barOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "55%",
            horizontal: true,
          },
        },
        colors: [PRIMARY_LIGHT],
        dataLabels: { enabled: false },
        xaxis: {
          categories: ["패션", "리빙", "식품", "뷰티", "가전", "기타"],
          labels: { style: { colors: MUTED } },
        },
        yaxis: {
          labels: { style: { colors: MUTED } },
        },
        grid: { borderColor: "#eef0f3" },
      },
      recentOrders: p14.orders.map((o) => ({ ...o })),
      hourlySeries: [{ name: "주문", data: [...p14.hourly] }],
      hourlyChartOptions: buildHourlyOptions(),
      dowSeries: [
        { name: "매출", data: [...p14.dowSales] },
        { name: "주문", data: [...p14.dowOrders] },
      ],
      dowChartOptions: buildDowOptions(),
      topProducts: p14.topProducts.map((r) => ({ ...r })),
      funnelSeries: [{ name: "건수", data: [...p14.funnel] }],
      funnelChartOptions: buildFunnelOptionsLegacy(),
      heatmapSeries: buildHeatmapSeries(heatSeed),
      heatmapOptions: buildHeatmapOptions(),
      pickerOptions: {
        shortcuts: [
          {
            text: "최근 7일",
            onClick(picker) {
              const e = new Date();
              e.setHours(0, 0, 0, 0);
              const s = new Date(e);
              s.setDate(s.getDate() - 6);
              picker.$emit("pick", [s, e]);
            },
          },
          {
            text: "최근 14일",
            onClick(picker) {
              const e = new Date();
              e.setHours(0, 0, 0, 0);
              const s = new Date(e);
              s.setDate(s.getDate() - 13);
              picker.$emit("pick", [s, e]);
            },
          },
          {
            text: "이번 달",
            onClick(picker) {
              const e = new Date();
              e.setHours(0, 0, 0, 0);
              const s = new Date(e.getFullYear(), e.getMonth(), 1);
              picker.$emit("pick", [s, e]);
            },
          },
          {
            text: "지난 달",
            onClick(picker) {
              const end = new Date();
              end.setDate(0);
              end.setHours(0, 0, 0, 0);
              const start = new Date(end.getFullYear(), end.getMonth(), 1);
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },
    };
  },
  computed: {
    donutOptions() {
      return buildDonutOptions(this.donutSeries);
    },
  },
  mounted() {
    this.syncMockFromRange();
  },
  methods: {
    onDateRangeChange() {
      this.syncMockFromRange();
    },
    onRefresh() {
      this.syncMockFromRange();
      this.$message.success("데이터를 갱신했습니다.");
    },
    onDemoExport() {
      this.$message.info("데모 환경에서는 내보내기를 지원하지 않습니다.");
    },
    onDemoReport() {
      this.$message.info("데모 환경에서는 리포트를 지원하지 않습니다.");
    },
    orderStatusTagType(status) {
      const map = {
        결제완료: "success",
        배송준비: "warning",
        배송중: "primary",
        취소요청: "danger",
      };
      return map[status] || "info";
    },
    resolvePresetKey(dayCount) {
      if (dayCount <= 7) {
        return "7d";
      }
      if (dayCount <= 16) {
        return "14d";
      }
      return "30d";
    },
    syncMockFromRange() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return;
      }
      const start = new Date(`${this.dateRange[0]}T00:00:00`);
      const end = new Date(`${this.dateRange[1]}T00:00:00`);
      const dayCount =
        Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
      if (dayCount < 1) {
        return;
      }

      const key = this.resolvePresetKey(dayCount);
      const preset = MOCK_PRESETS[key];

      this.kpis = preset.kpis.map((k) => ({ ...k }));
      this.donutSeries = [...preset.donut];
      this.barSeries = [{ name: "주문", data: [...preset.bar] }];
      this.recentOrders = preset.orders.map((o) => ({ ...o }));

      this.hourlySeries = [{ name: "주문", data: [...preset.hourly] }];
      this.dowSeries = [
        { name: "매출", data: [...preset.dowSales] },
        { name: "주문", data: [...preset.dowOrders] },
      ];
      this.topProducts = preset.topProducts.map((r) => ({ ...r }));
      this.funnelSeries = [{ name: "건수", data: [...preset.funnel] }];

      const n = Math.min(14, Math.max(1, dayCount));
      const curve = preset.revenue.slice(0, n);
      const revenueData =
        curve.length >= n
          ? curve
          : [
              ...curve,
              ...Array(n - curve.length).fill(curve[curve.length - 1]),
            ];

      const categories = buildCategories(start, end, n);
      this.lineSeries = [{ name: "매출", data: revenueData }];

      this.lineChartOptions = {
        ...this.lineChartOptions,
        xaxis: {
          ...this.lineChartOptions.xaxis,
          categories,
        },
      };

      const rangeLabel = `${this.dateRange[0]} ~ ${this.dateRange[1]}`;
      this.lineChartTitle = `매출 추이 · ${rangeLabel} (${dayCount}일)`;

      const a =
        parseInt(String(this.dateRange[0] || "").replace(/\D/g, ""), 10) || 0;
      const b =
        parseInt(String(this.dateRange[1] || "").replace(/\D/g, ""), 10) || 0;
      const heatSeed = dayCount * 97 + (a % 7000) + (b % 7000);
      this.heatmapSeries = buildHeatmapSeries(heatSeed);

      this.chartRevision += 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-dashboard {
  min-height: 100%;
  width: 100%;
  padding: 24px 32px 48px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f7f9fc 0%, #fff 32%);
}

.ops-dashboard__page-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebeef5;
}

.ops-dashboard__page-head-text {
  min-width: 0;
  flex: 1;
}

.ops-dashboard__page-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.03em;
}

.ops-dashboard__page-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #909399;
}

.ops-dashboard__page-head-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.ops-dashboard__head-btn {
  flex-shrink: 0;
}

.ops-dashboard__date-picker {
  width: 260px;
}

@media (min-width: 520px) {
  .ops-dashboard__date-picker {
    width: 280px;
  }
}

.ops-dashboard__refresh {
  flex-shrink: 0;
}

.ops-dashboard__section {
  margin-top: 8px;
  padding-top: 24px;
  border-top: 1px dashed #dcdfe6;
}

.ops-dashboard__section-title {
  margin: 0 0 18px;
  font-size: 17px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.ops-dashboard__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

@media (max-width: 992px) {
  .ops-dashboard__kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .ops-dashboard__kpis {
    grid-template-columns: 1fr;
  }

  .ops-dashboard__date-picker {
    width: 100%;
  }
}

.ops-dashboard__kpi-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.ops-dashboard__kpi-label {
  margin: 0 0 8px;
  font-size: 13px;
  color: #909399;
}

.ops-dashboard__kpi-value {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.ops-dashboard__kpi-delta {
  margin: 0;
  font-size: 12px;
}

.ops-dashboard__kpi-delta.is-up {
  color: #67c23a;
}

.ops-dashboard__kpi-delta.is-down {
  color: #e6a23c;
}

.ops-dashboard__row {
  margin-bottom: 20px !important;
}

/* lg(1200px) 미만에서 열이 세로로 쌓일 때 카드 사이 간격 (xs만 지정된 열은 기본 span 24로 스택) */
@media (max-width: 1199px) {
  .ops-dashboard__row > .el-col:not(:last-child) {
    margin-bottom: 20px;
  }
}

.ops-dashboard__chart-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 0;
}

.ops-dashboard__card-head {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.ops-dashboard__table {
  width: 100%;
}

.ops-dashboard__status-tag {
  border-radius: 4px;
  font-weight: 500;
}

.ops-dashboard__footnote {
  margin: 8px 4px 0;
  font-size: 12px;
  color: #909399;
  text-align: right;
}
</style>
